// src/features/chat/ScheduleSendControls.tsx
// Scheduled-send UI shared by both composers (ChatDetailPage and the entity
// rail panel): the ghost clock button with its Send-now / Schedule-send menu,
// the schedule popover (native datetime-local input + quick chips), the
// "Will be sent …" composer chip, and the "N scheduled" bar with its list.
// Data flow lives in ./scheduledMessages.ts — these components are pure UI.

import { useRef, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Button } from '@react/components';
import { AnchoredMenu, MenuItem } from '@/components/AnchoredMenu';
import { ghostIconBtn, messageId } from './panel/panelShared';
import {
  formatScheduledTime,
  parseLocalInputValue,
  presetInOneHour,
  presetTomorrowAtNine,
  toLocalInputValue,
  useScheduledMessages,
} from './scheduledMessages';

/** The chat composer is the only place that schedules sends, so the shared
 *  ghost button class lives in panelShared next to its siblings. */

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="8.25" />
      <path d="M12 7.5V12l3 1.8" />
    </svg>
  );
}

/**
 * The split-button's secondary half: a ghost clock opening "Send now" /
 * "Schedule send…". Confirming a time reports it up through onSchedule;
 * "Send now" reports null (back to normal mode).
 */
export function ScheduleSendButton({
  scheduledAt,
  onSchedule,
  disabled,
  align = 'end',
}: {
  scheduledAt: Date | null;
  onSchedule: (date: Date | null) => void;
  disabled?: boolean;
  align?: 'start' | 'end';
}) {
  const { t } = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const openPicker = () => {
    setMenuOpen(false);
    setError(false);
    setInputValue(toLocalInputValue(scheduledAt ?? presetInOneHour()));
    setPickerOpen(true);
  };

  const confirm = () => {
    const date = parseLocalInputValue(inputValue);
    if (!date || date.getTime() <= Date.now()) {
      setError(true);
      return;
    }
    setPickerOpen(false);
    onSchedule(date);
  };

  const quickChip =
    'rounded-full border border-border bg-muted/50 px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none';

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => setMenuOpen((v) => !v)}
        className={`${ghostIconBtn} ${scheduledAt ? 'text-primary' : ''}`}
        aria-label={t('chat.schedule.sendLater')}
        title={t('chat.schedule.sendLater')}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
      >
        <ClockIcon />
      </button>

      <AnchoredMenu
        anchor={buttonRef}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        align={align}
        ariaLabel={t('chat.schedule.sendLater')}
        className="min-w-40 py-1"
      >
        <MenuItem
          label={t('chat.schedule.sendNow')}
          onSelect={() => { setMenuOpen(false); onSchedule(null); }}
          icon={
            <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <path d="M4.5 12 19.5 4.5 12 19.5l-2-5.5-5.5-2Z" />
            </svg>
          }
        />
        <MenuItem
          label={t('chat.schedule.sendLater')}
          onSelect={openPicker}
          icon={<ClockIcon />}
        />
      </AnchoredMenu>

      <AnchoredMenu
        anchor={buttonRef}
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        align={align}
        role="dialog"
        ariaLabel={t('chat.schedule.sendLater')}
        className="w-64 p-3"
      >
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            <button type="button" className={quickChip} onClick={() => { setError(false); setInputValue(toLocalInputValue(presetInOneHour())); }}>
              {t('chat.schedule.inOneHour')}
            </button>
            <button type="button" className={quickChip} onClick={() => { setError(false); setInputValue(toLocalInputValue(presetTomorrowAtNine())); }}>
              {t('chat.schedule.tomorrowAtNine')}
            </button>
          </div>
          <input
            type="datetime-local"
            value={inputValue}
            onChange={(e) => { setError(false); setInputValue(e.target.value); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirm(); } }}
            aria-label={t('chat.schedule.custom')}
            aria-invalid={error}
            autoFocus
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          {error && (
            <p className="text-xs text-destructive" role="alert">
              {t('chat.schedule.invalidTime')}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setPickerOpen(false)}
              className="rounded-md px-2 py-1 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
            >
              {t('common.cancel')}
            </button>
            <Button size="sm" variant="primary" onClick={confirm}>
              {t('chat.schedule.confirm')}
            </Button>
          </div>
        </div>
      </AnchoredMenu>
    </>
  );
}

/** The composer chip once a schedule is armed: "Will be sent <date>", with an
 *  × that drops the composer back to normal mode. */
export function ScheduledChip({
  scheduledAt,
  locale,
  onClear,
}: {
  scheduledAt: Date;
  locale: string;
  onClear: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className="mb-2 flex items-center gap-2 rounded-md border border-border bg-muted px-2 py-1.5">
      <ClockIcon />
      <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
        {t('chat.schedule.willBeSent', { date: formatScheduledTime(scheduledAt.toISOString(), locale) })}
      </span>
      <button
        type="button"
        onClick={onClear}
        className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
        aria-label={t('chat.schedule.clear')}
        title={t('chat.schedule.clear')}
      >
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

/** Subtle bar above the composer: "N scheduled" opening the list of pending
 *  scheduled messages, each with a delete (cancel = DeleteMessage). Renders
 *  nothing when the conversation has none. */
export function ScheduledMessagesBar({
  conversationId,
  refreshKey,
  locale,
}: {
  conversationId: string;
  refreshKey?: number;
  locale: string;
}) {
  const { t } = useI18n();
  const { messages, cancel } = useScheduledMessages(conversationId, refreshKey);
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  if (!conversationId || messages.length === 0) return null;

  return (
    <div className="mb-2">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <ClockIcon />
        <span>{t('chat.schedule.scheduledCount', { count: messages.length })}</span>
      </button>

      <AnchoredMenu
        anchor={buttonRef}
        open={open}
        onClose={() => setOpen(false)}
        align="start"
        role="dialog"
        ariaLabel={t('chat.schedule.listTitle')}
        className="w-72 py-1"
      >
        <p className="px-3 py-1 text-xs font-medium text-muted-foreground">
          {t('chat.schedule.listTitle')}
        </p>
        <ul>
          {messages.map((m) => {
            const id = messageId(m);
            const snippet = (m?.content ?? '').trim();
            return (
              <li key={id} className="flex items-center gap-2 px-3 py-1.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-foreground" title={snippet}>
                    {snippet || '—'}
                  </p>
                  <p className="text-xs tabular-nums text-muted-foreground">
                    {formatScheduledTime(m?.scheduled_at, locale)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => void cancel(id)}
                  className={ghostIconBtn}
                  aria-label={t('common.delete')}
                  title={t('common.delete')}
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                  </svg>
                </button>
              </li>
            );
          })}
        </ul>
      </AnchoredMenu>
    </div>
  );
}
